import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController, ModalController, ToastController } from '@ionic/angular';
import { BaseClass } from '../../classes/base';
import { ProcessConfig } from '../../interfaces/process-config.interface';
import { TranslationService } from '../../services/translation.service';
import { UtilService } from '../../services/util.service';
import { Goal, GoalStatusEnum } from 'src/app/goals/_interfaces/goal.interface';
import { GoalsService } from 'src/app/goals/goals.service';
import { NewGoalsInput } from 'src/app/shared/modals/new-goals-input/new-goals-input';

@Component({
    selector: 'app-consent-goals',
    templateUrl: './consent-goals.component.html',
    styleUrls: ['./consent-goals.component.scss']
})
export class ConsentGoalsComponent extends BaseClass implements OnInit {

    @Input() language: string;
    @Input() processId: number;
    @Input() processConfig: ProcessConfig;

    public goals: Array<Goal> = [];
    public displayedGoals: Array<Goal> = [];
    public selectedLanguage: string;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            public alertController: AlertController,
            private _loadingController: LoadingController,
            private _modalController: ModalController,
            private _router: Router,
            private _goalsService: GoalsService,
            private _utilService: UtilService,
            private _activatedRoute: ActivatedRoute
    ) {
        super(_toastController, _translationService);
    }

    async ngOnInit() {
        this.selectedLanguage = this._translationService.getSelectedLanguage();
        await this._loadGoals();
    }

    public getColumnSize(questionsCount: number): number {
        let tableSize = 10;
        if (this.processConfig) {
            tableSize = this.processConfig.title ? tableSize - 2:tableSize;
            tableSize = this.processConfig.weight ? tableSize - 2:tableSize;
            tableSize = this.processConfig.deadline ? tableSize - 2:tableSize;
            tableSize = this.processConfig.description ? tableSize - 2:tableSize;
        }

        let columnSize = questionsCount ? parseInt((tableSize / questionsCount).toString()):null;

        if (columnSize)
            return columnSize;
        else
            return questionsCount > 1 ? 1:2;
    }

    public getValidationColumnSize(questionsCount: number): number {
        let tableSize = 12;
        if (this.processConfig) {
            tableSize = this.processConfig.title ? tableSize - 3:tableSize;
            tableSize = this.processConfig.weight ? tableSize - 4:tableSize;
            tableSize = this.processConfig.deadline ? tableSize - 2:tableSize;
            tableSize = this.processConfig.description ? tableSize - 2:tableSize;
        }

        let validationColumnSize = tableSize - this.getColumnSize(questionsCount) * questionsCount;

        if (validationColumnSize)
            return validationColumnSize;
        else
            return questionsCount > 1 ? 1:2;
    }

    public goBack(): void {
        this._utilService.removeProcessIdFromLocalStorage();
        this._router.navigate(['dashboard']);
    }

    ionViewDidLeave() {
        this._utilService.removeProcessIdFromLocalStorage();
    }

    public hasRejectGoals(): boolean {
        return this.goals && this.goals.some(goal => goal.status === GoalStatusEnum.Rejected);
    }

    public getTotalWeight(): number {
        return this.goals ? this.goals.reduce((sum, goal) => sum + (goal.weight ? goal.weight:0), 0):0;
    }

    public async confirmDeleteGoal(goal: Goal): Promise<void> {
        const alert = await this.alertController.create({
            message: this._translationService.getTranslation('goalProgressInfo.confirmDeleteGoal'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    this._deleteGoal(goal.id);
                }
            }]
        });

        await alert.present();
    }

    private _deleteGoal(goalId: number): void {
        const goalIndex = this.goals.findIndex(goal => goal.id === goalId);

        if (goalIndex !== -1) {
            this.goals.splice(goalIndex, 1);
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.goalDeleteSuccess'
                    ), 'success'
            );

        } else {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.goalNotFound'
                    ), 'danger'
            );
        }
    }

    public async openManageGoalsInputModal(goal: Goal = null) {
        if (!goal && this.goals && this.goals.length >= this.processConfig.maximumGoalQuantity) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.maxGoalsQuantityReached'
                    ), 'danger'
            );
            return;
        }

        const modal = await this._modalController.create({
            component: NewGoalsInput,
            cssClass: 'new-goals-input',
            componentProps: {
                'cycleParticipantId': 0,
                'processConfig': this.processConfig,
                'goal': goal,
                'consent': true
            }
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data) {
                let goal = dismissEvent.data;
                if (!goal.id && goal.id !== 0) {
                    goal.id = this.goals ? this.goals.length:0;
                    if (this.goals)
                        this.goals.push(dismissEvent.data);
                    else {
                        this.goals = [dismissEvent.data];
                        this.displayedGoals = [dismissEvent.data];
                    }
                } else {
                    this.goals = this.goals.map(g => (g.id === goal.id ? goal:g));
                    this.displayedGoals = this.displayedGoals.map(g => (g.id === goal.id ? goal:g));
                }
            }
        });

        return await modal.present();
    }


    private _validateWeights(goals: Array<Goal>): boolean {
        const weightSum = goals.reduce((sum, goal) => sum + goal.weight, 0);
        if (weightSum !== 100) {
            this.notify(
                    this._translationService.getTranslation(
                            'goalsInput.weightsWrongSum'
                    ), 'danger'
            );
            return false;
        }
        return true;
    }

    private async _loadGoals(): Promise<void> {
        if (this.processConfig.consentGoals && this.processConfig.consentGoals.length === 0) {
            const loading = await this._loadingController.create();
            await loading.present();
            this._goalsService.getConsentProcessGoals(
                    this.processId ? this.processId:0
            ).subscribe((response) => {
                this.goals = response.data;
                this.displayedGoals = response.data;
                loading.dismiss();
            }, (error) => {
                loading.dismiss();
                this.notify(this.getErrorNotification(error), 'danger');
            });
        } else {
            this.goals = this.processConfig.consentGoals;
            this.displayedGoals = this.processConfig.consentGoals;
        }
    }


    public changeSelectedLanguage(language: 'pt-BR' | 'en-US' | 'es-ES'): void {
        this.selectedLanguage = language;
    }

    public saveGoals(): void {
        const consentGoalsConfig = {
            consentGoalsGlobalizationConfig: this.processConfig.consentGoalsGlobalizationConfig,
            consentGoals: this.goals
        };
        this.dismissModal(consentGoalsConfig);
    }

    public dismissModal(data: any = null): void {
        this._modalController.dismiss(data);
    }
}